'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';

import { cn } from '@/app/utils/cn';
import { LinkButton } from '@/components/shared/CallToActionButton';

import { LogoImages } from './logoImages';
import { calculateOpacity } from './utils';

export const CaseStudyCarrousel = ({ caseStudies, logos, animationDuration }) => {
    const time = Number(animationDuration?.animationDuration || '4');
    // Refs
    const logosRef = useRef(null);
    const animationStarted = useRef(false);
    const animationIntervalRef = useRef(null);
    const timelineRef = useRef(null);

    // States
    const [activeIndex, setActiveIndex] = useState(0);
    const [mouseHover, setMouseHover] = useState(false);
    const [opacities, setOpacities] = useState([]);

    // Memoized values
    const caseSelected = useMemo(() => caseStudies?.[activeIndex], [caseStudies, activeIndex]);
    const showImage = useMemo(() => typeof caseSelected?.image === 'string', [caseSelected]);
    const testimonial = useMemo(() => caseSelected?.testimonial, [caseSelected]);
    const caseContainerStyles = useMemo(
        () =>
            cn(
                'h-full gap-8 grid grid-cols-1 sm:grid-cols-2 sm:gap-0',
                testimonial && 'sm:min-h-[26rem] xl:min-h-auto',
                !showImage && 'sm:pl-16 p-8 sm:p-16'
            ),
        [testimonial, showImage]
    );

    const startTimer = useCallback(() => {
        setMouseHover(false);
        animationIntervalRef.current = setInterval(() => {
            requestAnimationFrame(() => {
                const logos = logosRef.current?.children || [];
                const nextIndex = (activeIndex + 1) % logos.length;
                const nextLogo = logos[nextIndex]?.children[1];

                if (!nextLogo) {
                    return;
                }

                timelineRef.current.clear();
                timelineRef.current.set(nextLogo, { scaleX: 0 });
                timelineRef.current.to(nextLogo, {
                    scaleX: 0.97,
                    duration: time
                });
                setActiveIndex(nextIndex);
            });
        }, time * 1000);

        animationStarted.current = true;
    }, [time, activeIndex]);

    const stopTimer = useCallback(() => {
        setMouseHover(true);
        clearInterval(animationIntervalRef.current);
        animationStarted.current = false;
    }, []);

    useEffect(() => {
        timelineRef.current = gsap.timeline();

        return () => {
            timelineRef.current.clear();
        };
    }, [time]);

    useEffect(() => {
        if (logos) {
            setOpacities(calculateOpacity(logos, activeIndex));
        }

        if (!mouseHover && !animationStarted.current) {
            startTimer();
        }

        return stopTimer;
    }, [logos, activeIndex, mouseHover, startTimer, stopTimer]);

    return (
        <div
            className="container !mt-8 flex flex-col justify-center gap-6 md:flex-row"
            style={{ paddingBottom: '80px' }}
            onMouseEnter={() => stopTimer()}
            onMouseLeave={() => startTimer()}>
            <LogoImages
                logos={logos}
                logosRef={logosRef}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                sortedOpacities={opacities}
                timelineRef={timelineRef}
            />
            {!!caseSelected && (
                <div
                    className={`relative inline-block rounded-2xl bg-white text-blue sm:flex sm:items-center md:flex-1`}>
                    <div className={caseContainerStyles}>
                        <CaseCard
                            logo={caseSelected}
                            caseStudy={caseSelected}
                            hasTestimonial={!!testimonial}
                            hasImage={showImage}
                        />
                        {!!testimonial && <Testimonial testimonial={testimonial} />}
                        {showImage && (
                            <div className="relative size-full sm:w-auto sm:min-w-[19rem]">
                                <Image
                                    fill
                                    sizes="304px"
                                    src={caseSelected?.image}
                                    alt="brand logo"
                                    quality={15}
                                    className="object-cover"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const CaseCard = ({ logo, caseStudy, hasTestimonial, hasImage }) => {
    const { subheading, heading } = caseStudy;
    const { url, urlMap } = logo;

    const baseStyles = cn(
        'flex flex-1 flex-col gap-8 justify-between h-full w-full sm:gap-0',
        (hasTestimonial || hasImage) && 'sm:border-r sm:pr-6 sm:border-b-0',
        hasImage && 'sm:p-5 sm:m-0'
    );

    return (
        <div className={baseStyles}>
            <h4 className="mb-0 sm:mb-2">{heading}</h4>
            <p className="mb-0 text-sm sm:mb-2">{subheading}</p>
            <div className="flex flex-1 justify-start">
                {(urlMap || url) && (
                    <LinkButton
                        url={urlMap ?? url}
                        ariaLabel={`Learn More ${heading || `the content at ${url}`}`}
                        className="mt-auto capitalize hover:bg-blue hover:text-white">
                        <>
                            <span className="sr-only">{`Learn more about ${heading}`}</span>
                            Learn More
                        </>
                    </LinkButton>
                )}
            </div>
        </div>
    );
};

const Testimonial = ({ testimonial }) => {
    const firstTestimonial = testimonial?.[0];

    if (!firstTestimonial) {
        return null;
    }

    return (
        <div className="flex flex-col justify-start gap-4 sm:justify-center sm:gap-5 sm:pl-6">
            <div className="w-10">
                <Image
                    src="/assets/home/quote-symbol.svg"
                    width={0}
                    height={0}
                    sizes="40px"
                    className="h-auto w-full"
                    alt="brand logo"
                    unoptimized
                />
            </div>
            <p>{firstTestimonial?.quote}</p>
        </div>
    );
};
